import { useMutation } from "@apollo/client";
import { withErrorBoundary } from "@sentry/react";
import { Menu, MenuItem } from "@szhsin/react-menu";
import copy from "copy-to-clipboard";
import dompurify from "dompurify";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import {
  DeleteMeetingTranscriptsMutation,
  DeleteMeetingTranscriptsNewPageMutationMutationVariables,
  DeleteMeetingVideoRecordingMutationMutation,
  DeleteMeetingVideoRecordingMutationMutationVariables,
  GetMeetingTranscriptQueryQuery,
  SaveMeetingVideoRecordingMutationMutation,
  SaveMeetingVideoRecordingMutationMutationVariables,
} from "types/graphql-schema";

import TranscriptionParticipation from "@apps/meeting-transcription-dialog/participation";
import TranscriptionText from "@apps/meeting-transcription-dialog/transcription-text";
import { errorNotificationVar, successNotificationVar } from "@cache/cache";
import Button from "@components/button/button";
import { buttonTheme } from "@components/button/button-constants";
import Error from "@components/error/error";
import AppLink from "@components/link/link";
import Loading from "@components/loading/loading";
import Modal from "@components/modal/modal";
import { ModalWidth } from "@components/modal/modal-constants";
import { TopicflowIcon } from "@components/topicflow-icon/topicflow-icon";
import { classNames } from "@helpers/css";
import {
  assertEdgesNonNullWithStringId,
  assertNonNull,
  getUrl,
} from "@helpers/helpers";
import useConfirm from "@helpers/hooks/use-confirm";

import { MeetingViewEnum } from "../header-constants";
import {
  getMeetingVideoTimeFromSearch,
  updateMeetingSearch,
} from "../meeting-page-url-state";
import deleteMeetingTranscriptsNewPageMutation from "./graphql/delete-meeting-transcripts-mutation";
import deleteMeetingVideoRecordingMutation from "./graphql/delete-meeting-video-recording-mutation";
import saveMeetingVideoRecordingMutation from "./graphql/save-meeting-video-recording-mutation";

const HeaderedModal = ({
  open,
  title,
  onClose,
  topRightContent,
  fullHeight = false,
  children,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  topRightContent?: ReactNode;
  // When true the modal fills the viewport vertically (minus a top/bottom
  // margin); the header stays fixed at the top while the body scrolls.
  fullHeight?: boolean;
  children: ReactNode;
}) => (
  <Modal
    open={open}
    onClose={onClose}
    width={ModalWidth.medium}
    alignedTop={fullHeight}
    topMargin={!fullHeight}
    dialogClassName={fullHeight ? "h-[calc(100vh-4rem)] flex flex-col" : ""}
  >
    <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 shrink-0">
      <div className="text-base font-semibold text-gray-900">{title}</div>
      <div className="flex items-center gap-2">
        {topRightContent}
        <button
          type="button"
          onClick={onClose}
          aria-label="Close"
          className="p-1 rounded-md hover:bg-gray-100 text-gray-500"
        >
          <TopicflowIcon type="close" className="h-5 w-5" />
        </button>
      </div>
    </div>
    <div className={fullHeight ? "flex-1 min-h-0" : ""}>{children}</div>
  </Modal>
);

const MeetingSummaryGroupTranscription = ({
  meeting,
  meetingGroupId,
  isCurrentMeeting,
  refetch,
  loading,
  showVideoTranscriptModal,
  onCloseVideoTranscriptModal,
}: {
  meeting: NonNullable<GetMeetingTranscriptQueryQuery["meeting"]>;
  meetingGroupId: number;
  isCurrentMeeting: boolean;
  refetch: () => void;
  loading: boolean;
  showVideoTranscriptModal: boolean;
  onCloseVideoTranscriptModal: () => void;
}) => {
  const history = useHistory();
  const location = useLocation();
  const [showMore, setShowMore] = useState(false);
  const [deleteTranscript] = useMutation<
    DeleteMeetingTranscriptsMutation,
    DeleteMeetingTranscriptsNewPageMutationMutationVariables
  >(deleteMeetingTranscriptsNewPageMutation);
  const [saveVideoRecording, { loading: isSavingVideoRecording }] = useMutation<
    SaveMeetingVideoRecordingMutationMutation,
    SaveMeetingVideoRecordingMutationMutationVariables
  >(saveMeetingVideoRecordingMutation, {
    onCompleted: () => {
      successNotificationVar({
        title: "Video recording is being saved, please check back later.",
      });
      refetch();
    },
    onError: (error) => {
      errorNotificationVar({
        title: "Could not save video recording.",
        description: error.message,
      });
    },
  });
  const [deleteVideoRecording, { loading: isDeletingVideoRecording }] =
    useMutation<
      DeleteMeetingVideoRecordingMutationMutation,
      DeleteMeetingVideoRecordingMutationMutationVariables
    >(deleteMeetingVideoRecordingMutation, {
      onCompleted: () => {
        successNotificationVar({ title: "Video recording deleted." });
        refetch();
      },
      onError: (error) => {
        errorNotificationVar({
          title: "Could not delete video recording.",
          description: error.message,
        });
      },
    });
  const { ConfirmationDialog, confirm } = useConfirm(
    "Delete Transcript",
    "Are you sure you want to delete this transcript? This action cannot be undone.",
  );

  const transcript = meeting.transcript as
    | (NonNullable<typeof meeting.transcript> & {
        videoRecording?: string | null;
        canSaveVideoRecording?: boolean | null;
        videoRecordingAvailableUntil?: string | null;
      })
    | null
    | undefined;
  const orderedTranscriptData = transcript?.orderedTranscriptData;
  const highlightList = transcript?.highlightList
    ? assertEdgesNonNullWithStringId(transcript.highlightList)
    : [];
  const highlightCategories = transcript?.highlightCategories
    ? assertEdgesNonNullWithStringId(transcript.highlightCategories)
    : [];

  const videoRecording = transcript?.videoRecording ?? null;
  const recallVideoRecording = transcript?.recallVideoRecording ?? null;
  const videoSrc = (videoRecording || recallVideoRecording) ?? undefined;
  const canSaveVideoRecording = transcript?.canSaveVideoRecording ?? false;
  const videoRecordingAvailableUntil = transcript?.videoRecordingAvailableUntil
    ? new Date(transcript.videoRecordingAvailableUntil)
    : null;
  const videoAvailableUntilText = videoRecordingAvailableUntil
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: "medium",
        timeStyle: "short",
      }).format(videoRecordingAvailableUntil)
    : null;
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const pendingSeekRequestRef = useRef<{
    autoplay: boolean;
    seekTimeInSeconds: number;
  } | null>(null);
  const lastHandledVideoSeekRef = useRef<string | null>(null);
  const firstTranscriptTimestamp =
    orderedTranscriptData?.find(
      (
        transcriptData,
      ): transcriptData is NonNullable<typeof transcriptData> & {
        created: number;
      } => typeof transcriptData?.created === "number",
    )?.created ?? null;

  const handleCopyTranscript = useCallback(() => {
    copy(
      assertNonNull(
        meeting?.transcript?.orderedTranscriptData?.map(
          (transcriptData) =>
            `${transcriptData?.speaker}: ${transcriptData?.text}\n`,
        ),
      ).join("\n"),
    );
    successNotificationVar({ title: "Transcript copied.", timeout: 1000 });
  }, [meeting]);

  const handleDeleteTranscript = async () => {
    const confirmed = await confirm();
    if (confirmed) {
      await deleteTranscript({
        variables: {
          meetingId: meeting.id,
        },
      });
    }
  };

  const handleSaveVideoRecording = () => {
    if (isSavingVideoRecording) return;
    saveVideoRecording({
      variables: {
        meetingId: meeting.id,
      },
    });
  };

  const handleDeleteVideoRecording = () => {
    if (isDeletingVideoRecording) return;
    deleteVideoRecording({
      variables: {
        meetingId: meeting.id,
      },
    });
  };

  const seekVideo = useCallback(
    ({
      autoplay = false,
      seekTimeInSeconds,
    }: {
      autoplay?: boolean;
      seekTimeInSeconds: number;
    }) => {
      const video = videoRef.current;
      if (!video) return false;

      const performSeek = () => {
        const duration =
          Number.isFinite(video.duration) && video.duration > 0
            ? video.duration
            : null;
        const clampedSeekTime =
          duration === null
            ? seekTimeInSeconds
            : Math.min(seekTimeInSeconds, duration);

        video.currentTime = Math.max(clampedSeekTime, 0);
        video.scrollIntoView({ behavior: "smooth", block: "center" });

        if (autoplay) {
          void video.play().catch(() => {
            // Autoplay can be blocked on a fresh page load; keep the seek.
          });
        }
      };

      if (video.readyState >= 1) {
        performSeek();
        return true;
      }

      video.addEventListener("loadedmetadata", performSeek, { once: true });
      return true;
    },
    [],
  );

  const queueVideoSeek = useCallback(
    ({
      autoplay = false,
      seekTimeInSeconds,
    }: {
      autoplay?: boolean;
      seekTimeInSeconds: number;
    }) => {
      if (seekVideo({ autoplay, seekTimeInSeconds })) return;

      pendingSeekRequestRef.current = { autoplay, seekTimeInSeconds };
    },
    [seekVideo],
  );

  const setVideoElement = useCallback(
    (element: HTMLVideoElement | null) => {
      videoRef.current = element;
      if (!element) return;

      if (pendingSeekRequestRef.current) {
        const pendingSeekRequest = pendingSeekRequestRef.current;
        pendingSeekRequestRef.current = null;
        queueVideoSeek(pendingSeekRequest);
        return;
      }

      const deepLinkedVideoTime = getMeetingVideoTimeFromSearch(
        location.search,
      );
      if (deepLinkedVideoTime === null || !videoSrc) return;

      const deepLinkKey = `${meeting.id}:${videoSrc}:${deepLinkedVideoTime}`;
      if (lastHandledVideoSeekRef.current === deepLinkKey) return;

      lastHandledVideoSeekRef.current = deepLinkKey;
      queueVideoSeek({
        autoplay: true,
        seekTimeInSeconds: deepLinkedVideoTime,
      });
    },
    [location.search, meeting.id, queueVideoSeek, videoSrc],
  );

  const syncVideoTimestampToUrl = useCallback(
    (seekTimeInSeconds: number) => {
      const nextSearch = updateMeetingSearch({
        search: location.search,
        meetingView: isCurrentMeeting
          ? MeetingViewEnum.summary
          : MeetingViewEnum.previousMeetingSummary,
        videoTimeInSeconds: seekTimeInSeconds,
      });
      const normalizedSeekTime =
        getMeetingVideoTimeFromSearch(nextSearch) ?? seekTimeInSeconds;

      history.replace({
        pathname: location.pathname,
        search: nextSearch,
        hash: location.hash,
      });
      lastHandledVideoSeekRef.current = `${meeting.id}:${videoSrc ?? "pending"}:${normalizedSeekTime}`;
    },
    [
      history,
      isCurrentMeeting,
      location.hash,
      location.pathname,
      location.search,
      meeting.id,
      videoSrc,
    ],
  );

  // Clicking a transcript line seeks the video and opens the video modal so
  // the user can actually see the playback.
  const handleSkipToTranscriptTimestamp = useCallback(
    (seekTimeInSeconds: number) => {
      syncVideoTimestampToUrl(seekTimeInSeconds);
      queueVideoSeek({ autoplay: true, seekTimeInSeconds });
    },
    [queueVideoSeek, syncVideoTimestampToUrl],
  );

  useEffect(() => {
    const videoTimeInSeconds = getMeetingVideoTimeFromSearch(location.search);
    if (videoTimeInSeconds === null || !videoSrc) return;

    const deepLinkKey = `${meeting.id}:${videoSrc}:${videoTimeInSeconds}`;
    if (lastHandledVideoSeekRef.current === deepLinkKey) return;

    lastHandledVideoSeekRef.current = deepLinkKey;
    queueVideoSeek({ autoplay: true, seekTimeInSeconds: videoTimeInSeconds });
  }, [location.search, meeting.id, queueVideoSeek, videoSrc]);

  useEffect(() => {
    if (!videoSrc) return;

    const intervalId = window.setInterval(() => {
      const video = videoRef.current;
      if (!video || video.paused || video.ended) return;

      syncVideoTimestampToUrl(video.currentTime);
    }, 5000);

    return () => {
      window.clearInterval(intervalId);
    };
  }, [syncVideoTimestampToUrl, videoSrc]);

  if (!meeting.organization)
    return (
      <div className="text-gray-500 text-sm">
        The meeting is not associated to an existing organization. The notes
        and/or transcript cannot be summarized.
      </div>
    );

  const showVideoSection = videoRecording || canSaveVideoRecording;
  const hasTranscriptData =
    orderedTranscriptData && orderedTranscriptData.length > 0;
  const hasExpandableDetails =
    highlightList.length > 0 ||
    highlightCategories.length > 0 ||
    !!hasTranscriptData;

  return (
    <>
      <ConfirmationDialog />

      <div className="flex flex-col gap-6">
        {!isCurrentMeeting && (
          <div>
            <AppLink
              to={getUrl({
                meetingGroupId: meetingGroupId,
                meetingId: meeting.id,
              })}
              className="text-blue-link hover:underline font-normal text-sm"
            >
              Go to Last Meeting
            </AppLink>
          </div>
        )}

        {(meeting.transcript?.summary || meeting.notesSummary) && (
          <div
            className="text-gray-500 leading-6 whitespace-pre-line"
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(
                meeting.transcript?.summary || meeting.notesSummary || "",
              ),
            }}
          />
        )}
        {showMore && (
          <>
            <div className="text-gray-600 whitespace-pre-line empty:hidden">
              {highlightList.length > 0 ? (
                <ul className="list-disc ml-6 mt-2 space-y-3">
                  {highlightList.map((node) => (
                    <li key={node.id}>{node.highlight}</li>
                  ))}
                </ul>
              ) : highlightCategories.length > 0 ? (
                <ul className="mt-2 space-y-3">
                  {highlightCategories.map((node) => (
                    <li key={node.id}>
                      <span className="font-semibold text-gray-900">
                        {node.categoryTitle}
                      </span>
                      <ul className="list-disc ml-6 mt-2 space-y-3">
                        {assertEdgesNonNullWithStringId(node.highlights).map(
                          (highlightNode) => (
                            <li key={highlightNode.id}>
                              {highlightNode.highlight}
                            </li>
                          ),
                        )}
                      </ul>
                    </li>
                  ))}
                </ul>
              ) : meeting.transcript?.summary || meeting.notesSummary ? null : (
                <span>No highlights.</span>
              )}
            </div>
            <TranscriptionParticipation meeting={meeting} />
          </>
        )}
        {hasExpandableDetails && (
          <button
            type="button"
            onClick={() => setShowMore(!showMore)}
            className={classNames(
              "self-start text-xs font-medium text-gray-500 flex items-center gap-1 py-1 px-2 rounded-md -ml-2 hover:bg-gray-100",
              // Collapsed: nudge the button closer to the paragraph (16px gap
              // instead of the 24px gap-6 that separates expanded sections).
              !showMore && "-mt-2",
            )}
          >
            {showMore ? "Show less" : "Show more"}
            <TopicflowIcon
              type={showMore ? "minus" : "plus"}
              className="h-4 w-4"
            />
          </button>
        )}
      </div>

      {(showVideoSection || hasTranscriptData) && (
        <HeaderedModal
          open={showVideoTranscriptModal}
          title="Video and Transcript"
          onClose={onCloseVideoTranscriptModal}
          fullHeight
          topRightContent={
            (hasTranscriptData ||
              (!videoRecording && canSaveVideoRecording)) && (
              <div className="flex items-center gap-2">
                {!videoRecording &&
                  canSaveVideoRecording &&
                  videoAvailableUntilText && (
                    <div className="text-xs text-gray-500">
                      Available to save video until {videoAvailableUntilText}
                    </div>
                  )}
                <Menu
                  portal={false}
                  className="z-dropdown "
                  align="end"
                  transition={false}
                  aria-label="Video and transcript dropdown menu list"
                  menuButton={
                    <button
                      className="text-gray-400 rounded p-1 hover:bg-gray-100"
                      data-testid="transcript-dropdown-button"
                      aria-label="Video and transcript dropdown menu button"
                    >
                      <span className="sr-only">Open options</span>
                      <TopicflowIcon
                        type="horizontalDots"
                        className="h-5 w-5"
                      />
                    </button>
                  }
                >
                  {!videoRecording && canSaveVideoRecording && (
                    <MenuItem
                      className="text-sm"
                      onClick={handleSaveVideoRecording}
                      disabled={isSavingVideoRecording}
                    >
                      Save Video Recording
                    </MenuItem>
                  )}
                  {hasTranscriptData && (
                    <MenuItem
                      className="text-sm"
                      onClick={handleCopyTranscript}
                    >
                      Copy Transcript
                    </MenuItem>
                  )}
                  {hasTranscriptData && (
                    <MenuItem
                      className="text-sm"
                      onClick={handleDeleteTranscript}
                    >
                      Delete Entire Transcript
                    </MenuItem>
                  )}
                </Menu>
              </div>
            )
          }
        >
          <div
            className={classNames(
              "h-full divide-gray-200 overflow-hidden",
              // Tall viewports: stack video on top, transcript fills the
              // remaining height and scrolls independently while the video
              // stays pinned at the top.
              "flex flex-col divide-y",
              // Shorter viewports: switch to a side-by-side layout so the
              // transcript stays visible alongside the video, with each
              // column scrolling independently.
              "[@media(max-height:1000px)]:flex-row",
              "[@media(max-height:1000px)]:divide-y-0",
              "[@media(max-height:1000px)]:divide-x",
            )}
          >
            {showVideoSection && (
              <div className="px-6 py-4 flex flex-col gap-4 [@media(max-height:1000px)]:flex-1 [@media(max-height:1000px)]:min-w-0 [@media(max-height:1000px)]:overflow-y-auto">
                {videoSrc && (
                  <>
                    <video
                      ref={setVideoElement}
                      key={videoRecording || recallVideoRecording}
                      controls
                      onSeeked={(e) =>
                        syncVideoTimestampToUrl(e.currentTarget.currentTime)
                      }
                      preload="metadata"
                      className="w-full aspect-video rounded-lg border border-gray-200 bg-black"
                      src={videoSrc}
                    />
                    {videoRecording && (
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          text="Delete Recording"
                          theme={buttonTheme.textRed}
                          onClick={handleDeleteVideoRecording}
                          disabled={isDeletingVideoRecording}
                          tooltip="Remove the saved video from Topicflow"
                        />
                        {isDeletingVideoRecording && (
                          <Loading mini size={5} className="text-gray-500" />
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            )}
            {hasTranscriptData && (
              <div className="px-6 py-1 flex-1 min-h-0 min-w-0 overflow-y-auto">
                <div className="mt-4">
                  <TranscriptionText
                    meeting={meeting}
                    onSkipToVideoTime={
                      videoSrc ? handleSkipToTranscriptTimestamp : undefined
                    }
                    transcriptStartTimeMs={firstTranscriptTimestamp}
                  />
                </div>
                <div className="mt-4 mb-4 flex justify-center gap-2">
                  <Button
                    onClick={() => refetch()}
                    disabled={loading}
                    text="Refresh"
                  />
                  <Loading
                    mini
                    size={5}
                    className={loading ? "opacity-100" : "opacity-0"}
                  />
                </div>
              </div>
            )}
          </div>
        </HeaderedModal>
      )}
    </>
  );
};

const MeetingSummaryGroupTranscriptionWithSentry = withErrorBoundary(
  MeetingSummaryGroupTranscription,
  {
    fallback: (
      <Error description={"The meeting summary could not be rendered."} />
    ),
  },
);

export default MeetingSummaryGroupTranscriptionWithSentry;
